// IssueTab — the orchestrator for the Issues surface. Lays out the summary
// strip + filter bar over a two-column register (left) / detail drawer (right),
// per the design wireframe. Owns the filter + search state, the selected-issue
// state (seeded from the deep-link ?item= param), and the create / edit form
// flow. Wires the issue command hooks (create / update / transition / close /
// append comment+action / mark-action-done) into the drawer's callbacks.
//
// Role gating: viewers get a read-only tab (no Add / Edit / composer /
// transition / close controls); editors and admins get full write. The API is
// editor-gated server-side, but the UX must match capability — never render a
// control the user can't action.
//
// The list feeds the register + summary; the selected issue is fetched fresh
// via useTrackingItem so the drawer always shows the latest thread + actions.
//
// Viewport-fit: a useLayoutEffect measures the grid container's distance from
// the viewport top, then sets a max-height that exactly fills the remaining
// viewport height. Re-measurement is driven by:
//   1. A ResizeObserver on document.body — fires whenever any element above the
//      grid changes height (conflict/error alerts appearing, the linked-filter
//      banner toggling, chip rows wrapping) without needing those states in deps.
//   2. A window 'resize' event listener for viewport dimension changes.
//   3. The [issues] dep so the initial load (IssueSummaryStrip 0→107px) re-fires.
// Both columns are overflow-y-auto at all breakpoints so mobile (stacked) and
// desktop (side-by-side) each scroll their own column independently.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../../../../contexts/AuthContext";
import { useUserDisplays } from "../../../../hooks/useUsers";
import { useOrgUsers } from "../../../../hooks/useOrgUsers";
import { useQueryClient } from "@tanstack/react-query";
import { isConflictError } from "../../../../services/api";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import {
  useTrackingItems,
  useTrackingItem,
  useCreateTrackingItem,
  useUpdateTrackingItem,
  useTransitionIssue,
  useCloseIssue,
  useAppendThreadEntry,
  useMarkActionDone,
} from "../../../../hooks/useTracking";
import { trackingKeys } from "../../../../hooks/trackingKeys";
import Alert from "../../../common/Alert";
import Button from "../../../common/Button";
import IssueSummaryStrip from "./IssueSummaryStrip";
import IssueFilterChipBar from "./IssueFilterChipBar";
import IssueRegisterTable from "./IssueRegisterTable";
import IssueFormModal from "./IssueFormModal";
import IssueDetailDrawer from "./drawer/IssueDetailDrawer";
import {
  applyIssueChipFilters,
  EMPTY_ISSUE_FILTERS,
  UNASSIGNED_OWNER_TOKEN,
  type IssueChipFilters,
} from "./applyIssueChipFilters";
import type {
  AppendThreadActionInput,
  CreateIssueInput,
  Issue,
  IssueCloseCommand,
  IssueLinkTargetType,
  IssueSeverity,
  IssueTransitionCommand,
  UpdateIssueInput,
} from "../../../../types";
import type { IssueFormInitialValues } from "./IssueFormModal";
import { useSearchParams } from "react-router";

const LINKED_TARGET_TYPES = new Set<IssueLinkTargetType>([
  "deliverable",
  "milestone",
  "kpi",
  "kpi-measurement",
]);

// Raise-from-signal: source-type → suggested title prefix / severity. A KPI
// breach is critical by default (the metric failed); a rejected deliverable
// or overdue milestone is high (recoverable but actionable). The
// supplier-party default reflects that all three failing-signal sources sit
// in the supplier's lane; the user can change the party in the form. The
// auto-link points at the source item so back-links and the linked-to
// filter are immediately consistent.
const RAISE_PREFILL: Record<
  IssueLinkTargetType,
  { titlePrefix: string; severity: IssueSeverity }
> = {
  deliverable: { titlePrefix: "Issue raised from deliverable", severity: "high" },
  milestone: { titlePrefix: "Issue raised from milestone", severity: "high" },
  kpi: { titlePrefix: "Issue raised from KPI", severity: "critical" },
  "kpi-measurement": {
    titlePrefix: "Issue raised from KPI period",
    severity: "critical",
  },
};

function buildRaiseInitialValues(
  srcType: IssueLinkTargetType,
  srcId: string,
  srcTitle: string | null,
): IssueFormInitialValues {
  const cfg = RAISE_PREFILL[srcType];
  const titleSuffix = srcTitle ? `: ${srcTitle}` : "";
  return {
    title: `${cfg.titlePrefix}${titleSuffix}`,
    severity: cfg.severity,
    party: "supplier",
    links: [{ targetType: srcType, targetId: srcId }],
  };
}

const LINKED_TARGET_LABELS: Record<IssueLinkTargetType, string> = {
  deliverable: "Deliverable",
  milestone: "Milestone",
  kpi: "KPI",
  "kpi-measurement": "KPI period",
};

function parseLinkedTargetType(value: string | null): IssueLinkTargetType | null {
  if (!value) return null;
  return LINKED_TARGET_TYPES.has(value as IssueLinkTargetType)
    ? (value as IssueLinkTargetType)
    : null;
}

const WRITE_ROLES = new Set(["admin", "editor"]);

const CONFLICT_MESSAGE =
  "This issue changed since you loaded it. We've refreshed it — review the latest state and try again.";

const MUTATION_ERROR_MESSAGE =
  "Something went wrong saving that change. Please try again.";

// Minimum height for the list/drawer region. Floors the viewport-derived bound
// so the drawer's thread never collapses to a sliver on short laptops; below
// this the page scrolls rather than starving the thread. Tuned so the thread
// gets a readable share once the fixed fields + composer are accounted for.
const GRID_MIN_HEIGHT = "44rem";

export interface IssueTabProps {
  contractId: string;
  itemIdFromUrl: string | null;
  /** ?linkedType= from the URL — set by the back-link badge on a
   *  deliverable / milestone / KPI / KPI-measurement row to pre-apply the
   *  "Linked to" filter. Independent of `itemIdFromUrl`; both can be set. */
  linkedTypeFromUrl?: string | null;
  linkedIdFromUrl?: string | null;
  /** Navigates to a linked register item by setting ?tab=<slug>&item=<id>.
   *  Threaded from ManagedContractDetail → IssueTab → IssueDetailDrawer. */
  onNavigateToLinkedItem?: (
    targetType: IssueLinkTargetType,
    targetId: string,
  ) => void;
}

export default function IssueTab({
  contractId,
  itemIdFromUrl,
  linkedTypeFromUrl = null,
  linkedIdFromUrl = null,
  onNavigateToLinkedItem,
}: IssueTabProps) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.has(user?.role ?? "");

  const listQuery = useTrackingItems(contractId, "issue");
  const issues = useMemo<Issue[]>(() => {
    const items = listQuery.data?.items ?? [];
    return items.filter((i): i is Issue => i.type === "issue");
  }, [listQuery.data]);

  // Seed the linked filter from the deep-link on first mount and whenever the
  // URL pair changes — keeps the surface honest if the user re-navigates from
  // a different back-link badge in the same session.
  const initialLinkedFilter = useMemo(() => {
    const parsedType = parseLinkedTargetType(linkedTypeFromUrl);
    if (!parsedType || !linkedIdFromUrl) return null;
    return { targetType: parsedType, targetId: linkedIdFromUrl };
  }, [linkedTypeFromUrl, linkedIdFromUrl]);

  const [filters, setFilters] = useState<IssueChipFilters>({
    ...EMPTY_ISSUE_FILTERS,
    linked: initialLinkedFilter,
  });
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(itemIdFromUrl);
  const [formMode, setFormMode] = useState<"create" | "edit" | null>(null);
  const [raiseInitial, setRaiseInitial] = useState<IssueFormInitialValues | null>(null);
  const [conflict, setConflict] = useState(false);
  const [mutationError, setMutationError] = useState(false);

  // Viewport-fit: measure the grid container's distance from the viewport top
  // so we can cap its max-height to exactly the remaining viewport space.
  // This prevents the page-level scrollbar that appears when the drawer is tall
  // (long thread + composer). The 2.5rem subtracted is main's bottom padding
  // (py-8 = 2rem) + a sub-pixel rounding buffer so the grid never bleeds into
  // the page gutter.
  //
  // Re-measurement fires from three sources:
  //   - ResizeObserver on document.body: any height change above the grid
  //     (conflict/error alerts, linked-filter banner, chip-row wrap) triggers a
  //     re-measure without needing those as explicit deps.
  //   - window 'resize': viewport dimension changes.
  //   - [issues] dep: IssueSummaryStrip expands from 0 → ~107px when data
  //     arrives, shifting the grid down — the effect must re-run after that.
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridMaxH, setGridMaxH] = useState<string | undefined>(undefined);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const update = () => {
      // scrollY=0 at initial render; include it so the formula stays correct
      // if the page is scrolled programmatically before this fires.
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Floor the bound so the drawer's thread stays usable. On a tall page
      // header (title + tabs + summary strip + filter bar can claim ~430px),
      // the available viewport slice alone squeezes the thread to a sliver, so
      // the grid never drops below GRID_MIN_HEIGHT — accepting a modest page
      // scroll on short laptops in exchange for a readable thread, while large
      // monitors stay fully within the viewport.
      setGridMaxH(`max(calc(100vh - ${top}px - 2.5rem), ${GRID_MIN_HEIGHT})`);
    };
    update();
    window.addEventListener("resize", update);
    // Observe the body content box so any height change caused by elements
    // appearing or disappearing above the grid (alerts, banners, chip-wrap)
    // triggers a re-measure. Guard for jsdom environments without RO support.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(document.body);
    }
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
    // Re-measure when issues load (IssueSummaryStrip height change).
  }, [issues]);

  // Raise-from-signal trigger: a button on a failing register row navigates
  // here with ?raise=1&srcType=…&srcId=…[&srcTitle=…]. Pattern 0 derives the
  // raise state during render; a separate setState-free effect strips the raise
  // params from the URL (setSearchParams is a real URL-mutation side effect that
  // must remain in an effect).
  const [searchParams, setSearchParams] = useSearchParams();
  const raiseParam = searchParams.get("raise");
  const raiseSrcType = raiseParam === "1"
    ? parseLinkedTargetType(searchParams.get("srcType"))
    : null;
  const raiseSrcId = raiseParam === "1" ? searchParams.get("srcId") : null;
  const raiseSrcTitle = raiseParam === "1" ? searchParams.get("srcTitle") : null;

  // Compute a stable key so the Pattern 0 guard fires exactly once per raise
  // signal, not on every render while the params are still present.
  const raiseSignalKey = raiseSrcType && raiseSrcId
    ? `${raiseSrcType}:${raiseSrcId}`
    : null;
  const [seededRaiseFor, setSeededRaiseFor] = useState<string | null>(null);
  if (raiseSignalKey !== null && raiseSignalKey !== seededRaiseFor) {
    setSeededRaiseFor(raiseSignalKey);
    setRaiseInitial(buildRaiseInitialValues(raiseSrcType!, raiseSrcId!, raiseSrcTitle));
    setFormMode("create");
  }

  // Strip the raise params from the URL after they have been consumed so a
  // refresh after dismiss does not re-open the form. This is the only reason
  // an effect is still needed here — setSearchParams is a URL-mutation side
  // effect that cannot run during render.
  useEffect(() => {
    if (raiseParam !== "1" || !raiseSrcType || !raiseSrcId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("raise");
    next.delete("srcType");
    next.delete("srcId");
    next.delete("srcTitle");
    setSearchParams(next, { replace: true });
  }, [raiseParam, raiseSrcType, raiseSrcId, searchParams, setSearchParams]);

  const queryClient = useQueryClient();

  // Pattern 0: keep the selection in step with the deep-link param as it
  // changes across navigations.
  const [prevItemIdFromUrl, setPrevItemIdFromUrl] = useState(itemIdFromUrl);
  if (itemIdFromUrl !== prevItemIdFromUrl) {
    setPrevItemIdFromUrl(itemIdFromUrl);
    if (itemIdFromUrl) setSelectedId(itemIdFromUrl);
  }

  // Pattern 0: apply or clear the linked filter when the URL pair changes,
  // while preserving all other chip selections.
  const [prevLinkedFilter, setPrevLinkedFilter] = useState(initialLinkedFilter);
  if (initialLinkedFilter !== prevLinkedFilter) {
    const isSame =
      prevLinkedFilter &&
      initialLinkedFilter &&
      prevLinkedFilter.targetType === initialLinkedFilter.targetType &&
      prevLinkedFilter.targetId === initialLinkedFilter.targetId;
    if (!isSame) {
      setPrevLinkedFilter(initialLinkedFilter);
      setFilters((prev) => ({ ...prev, linked: initialLinkedFilter }));
    }
  }

  const clearLinkedFilter = () =>
    setFilters((prev) => ({ ...prev, linked: null }));

  // Resolve the linked target's display name for the banner. Without this the
  // user lands at "Showing issues linked to Deliverable 01HXYZ…" which is not
  // actionable — they came from a row whose name they already knew. The
  // register query is enabled only when the linked filter is set, so the
  // request only fires for users who actually arrived via a back-link badge.
  // The query is React-Query-cached so a user who clicked through from the
  // picker pays nothing.
  const linkedRegisterQuery = useTrackingItems(
    contractId,
    filters.linked?.targetType ?? "deliverable",
    undefined,
    { enabled: filters.linked !== null },
  );
  const linkedTargetName = useMemo<string | null>(() => {
    if (!filters.linked) return null;
    const items = linkedRegisterQuery.data?.items ?? [];
    const match = items.find((i) => i.id === filters.linked!.targetId);
    return match?.name ?? null;
  }, [filters.linked, linkedRegisterQuery.data]);

  // All command 409s are handled identically — version conflict, an illegal
  // transition (only reachable once a concurrent change has moved the issue
  // on, since the UI guard otherwise blocks it), and a duplicate action-done
  // all mean the issue changed under us. Refetch the item (the thread is
  // embedded on it, so this refreshes the thread too) and surface a banner
  // prompting a review + retry — the deliverable optimistic-lock flow. 400 /
  // 403 are plain validation / permission errors and fall through to the
  // mutation's own rejection.
  const handleMutationError = (error: unknown, itemId: string) => {
    if (isConflictError(error)) {
      setConflict(true);
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(contractId, "issue", itemId),
      });
      return;
    }
    // Non-409 (validation / permission / server) — surface it rather than
    // letting the composer swallow it. A failed comment or status update that
    // shows nothing reads to the user as "it posted but vanished".
    setMutationError(true);
  };

  // Age-bucket filtering anchors to the Brisbane calendar day (matching the
  // displayed derivations), not the browser wall-clock. A UTC-midnight Date on
  // the Brisbane day feeds applyIssueChipFilters' startOfUTCDay math correctly.
  const filtered = useMemo(
    () =>
      applyIssueChipFilters(
        issues,
        filters,
        search,
        new Date(`${getBrisbaneToday()}T00:00:00Z`),
      ),
    [issues, filters, search],
  );

  // Distinguishes a filter miss from a never-populated register so the empty
  // state can offer the right next step. Any chip selection, the linked-item
  // filter, or a non-blank search counts as active.
  const hasActiveFilters =
    filters.status.length > 0 ||
    filters.severity.length > 0 ||
    filters.owner.length > 0 ||
    filters.age.length > 0 ||
    filters.linked !== null ||
    search.trim() !== "";

  // Owner options for the filter chip + form: resolved names for every owner
  // that appears on an issue, plus the Unassigned sentinel.
  const ownerIds = useMemo(
    () =>
      Array.from(
        new Set(issues.map((i) => i.ownerId).filter((id): id is string => !!id)),
      ),
    [issues],
  );
  const { data: owners } = useUserDisplays(ownerIds);
  const ownerOptions = useMemo(() => {
    const opts = ownerIds.map((id) => ({
      value: id,
      label: owners.get(id)?.name ?? id,
    }));
    return [...opts, { value: UNASSIGNED_OWNER_TOKEN, label: "Unassigned" }];
  }, [ownerIds, owners]);

  // The create / edit form offers every ACTIVE org user as a possible owner
  // (admins + editors + viewers) — an issue owner is an assignee, so the list
  // must come from the org's people, not from whoever already owns an issue
  // (that was a chicken-and-egg: a never-assigned user could never be picked).
  // The form has its own blank `<option value="">` for "no owner", so the
  // Unassigned sentinel is deliberately omitted here. Sorted by name.
  const { data: orgUsers } = useOrgUsers();
  const formOwnerOptions = useMemo(() => {
    const items = orgUsers?.items ?? [];
    return items
      .filter((u) => u.status !== "disabled")
      .map((u) => ({ value: u.userId, label: u.name || u.email }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [orgUsers]);

  const selectedItemQuery = useTrackingItem(
    contractId,
    "issue",
    selectedId ?? "",
  );
  const selectedIssue =
    selectedItemQuery.data && selectedItemQuery.data.type === "issue"
      ? (selectedItemQuery.data as Issue)
      : undefined;

  const createMutation = useCreateTrackingItem();
  const updateMutation = useUpdateTrackingItem();
  const transitionMutation = useTransitionIssue();
  const closeMutation = useCloseIssue();
  const appendMutation = useAppendThreadEntry();
  const markDoneMutation = useMarkActionDone();

  const isMutating =
    transitionMutation.isPending ||
    closeMutation.isPending ||
    appendMutation.isPending ||
    markDoneMutation.isPending;

  const handleCreate = async (input: CreateIssueInput) => {
    const created = await createMutation.mutateAsync({
      contractId,
      type: "issue",
      input,
    });
    setFormMode(null);
    setRaiseInitial(null);
    if (created && created.type === "issue") setSelectedId(created.id);
  };

  const handleEdit = async (input: UpdateIssueInput) => {
    if (!selectedIssue) return;
    await updateMutation.mutateAsync({
      contractId,
      type: "issue",
      itemId: selectedIssue.id,
      input,
    });
    setFormMode(null);
  };

  const handleTransition = (input: IssueTransitionCommand) => {
    if (!selectedIssue) return;
    setMutationError(false);
    transitionMutation.mutate(
      {
        contractId,
        itemId: selectedIssue.id,
        body: { ...input, version: selectedIssue.version },
      },
      { onError: (error) => handleMutationError(error, selectedIssue.id) },
    );
  };

  const handleClose = (input: IssueCloseCommand) => {
    if (!selectedIssue) return;
    setMutationError(false);
    closeMutation.mutate(
      {
        contractId,
        itemId: selectedIssue.id,
        body: { ...input, version: selectedIssue.version },
      },
      { onError: (error) => handleMutationError(error, selectedIssue.id) },
    );
  };

  const handleAddComment = (text: string) => {
    if (!selectedIssue) return;
    setMutationError(false);
    appendMutation.mutate(
      {
        contractId,
        itemId: selectedIssue.id,
        kind: "comment",
        input: { text },
      },
      { onError: (error) => handleMutationError(error, selectedIssue.id) },
    );
  };

  const handleAddAction = (input: AppendThreadActionInput) => {
    if (!selectedIssue) return;
    setMutationError(false);
    appendMutation.mutate(
      {
        contractId,
        itemId: selectedIssue.id,
        kind: "action",
        input,
      },
      { onError: (error) => handleMutationError(error, selectedIssue.id) },
    );
  };

  const handleMarkActionDone = (entryId: string) => {
    if (!selectedIssue) return;
    setMutationError(false);
    markDoneMutation.mutate(
      {
        contractId,
        itemId: selectedIssue.id,
        entryId,
        body: {},
      },
      { onError: (error) => handleMutationError(error, selectedIssue.id) },
    );
  };

  return (
    <div className="space-y-4">
      {conflict && (
        <Alert variant="warning" onDismiss={() => setConflict(false)}>
          {CONFLICT_MESSAGE}
        </Alert>
      )}
      {mutationError && (
        <Alert variant="error" onDismiss={() => setMutationError(false)}>
          {MUTATION_ERROR_MESSAGE}
        </Alert>
      )}
      {filters.linked && (
        <div
          data-testid="issue-linked-filter-banner"
          className="flex items-center justify-between gap-3 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-body-sm text-brand-800 dark:border-brand-900 dark:bg-brand-950/40 dark:text-brand-200"
        >
          <span>
            Showing issues linked to {LINKED_TARGET_LABELS[filters.linked.targetType]}{" "}
            {linkedTargetName && (
              <span className="font-medium">{linkedTargetName}</span>
            )}{" "}
            <span className="font-mono text-caption">({filters.linked.targetId})</span>.
          </span>
          <button
            type="button"
            onClick={clearLinkedFilter}
            className="rounded px-2 py-0.5 text-caption font-medium text-brand-700 hover:bg-brand-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 dark:text-brand-200 dark:hover:bg-brand-900"
          >
            Clear filter
          </button>
        </div>
      )}
      <IssueSummaryStrip issues={issues} />

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <IssueFilterChipBar
            filters={filters}
            onFiltersChange={setFilters}
            search={search}
            onSearchChange={setSearch}
            ownerOptions={ownerOptions}
          />
        </div>
        {canWrite && (
          <div className="shrink-0">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setFormMode("create")}
            >
              Add issue
            </Button>
          </div>
        )}
      </div>

      {/* Two-column layout. On desktop (lg+) the list and drawer sit side by
          side; on mobile (<lg) they stack. Each column has overflow-y-auto at
          all breakpoints so both the stacked and side-by-side layouts scroll
          within the viewport-bounded container (min-h-0 on every child allows
          overflow-y-auto to actually clamp; CSS grid rows would ignore
          max-height on the container, which is why we use flex). */}
      <div
        ref={gridRef}
        data-testid="issue-layout-grid"
        className="flex flex-col gap-4 lg:flex-row"
        style={gridMaxH ? { maxHeight: gridMaxH } : undefined}
      >
        <div className="min-h-0 overflow-y-auto lg:flex-[1.35]">
          <IssueRegisterTable
            issues={filtered}
            selectedId={selectedId}
            onSelect={setSelectedId}
            hasActiveFilters={hasActiveFilters}
            canRaise={canWrite}
            onRaiseFirst={() => setFormMode("create")}
          />
        </div>
        <div
          data-testid="issue-drawer"
          className="min-h-0 overflow-y-auto rounded-lg border border-slate-200 bg-white lg:flex-1 dark:border-slate-700 dark:bg-slate-800/40"
        >
          {selectedIssue ? (
            <IssueDetailDrawer
              issue={selectedIssue}
              contractId={contractId}
              canWrite={canWrite}
              isMutating={isMutating}
              onEditIssue={() => setFormMode("edit")}
              onTransition={handleTransition}
              onClose={handleClose}
              onAddComment={handleAddComment}
              onAddAction={handleAddAction}
              onMarkActionDone={handleMarkActionDone}
              onNavigateToLinkedItem={onNavigateToLinkedItem}
            />
          ) : (
            <div className="p-8 text-center text-body-sm text-slate-500 dark:text-slate-400">
              Select an issue to see its detail.
            </div>
          )}
        </div>
      </div>

      {formMode === "create" && (
        <IssueFormModal
          mode="create"
          contractId={contractId}
          ownerOptions={formOwnerOptions}
          initialValues={raiseInitial ?? undefined}
          onSubmit={handleCreate}
          onClose={() => {
            setFormMode(null);
            setRaiseInitial(null);
          }}
          isSubmitting={createMutation.isPending}
        />
      )}
      {formMode === "edit" && selectedIssue && (
        <IssueFormModal
          mode="edit"
          issue={selectedIssue}
          contractId={contractId}
          ownerOptions={formOwnerOptions}
          onSubmit={handleEdit}
          onClose={() => setFormMode(null)}
          isSubmitting={updateMutation.isPending}
        />
      )}
    </div>
  );
}
